import { Fragment, useCallback, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import cx from 'classnames'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isObject from 'lodash/isObject'
import Link from 'next/link'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'
import Masonry from 'react-masonry-css'

import { resetBodyScrollOnNavigate } from 'common/utils/modalScroll'
import classNames from './styles.module.scss'
import containerClassNames from 'common/widgets/Container/styles.module.scss'


const breakpointCols = { default: 5, 1400: 4, 1280: 3, 1024: 1 }

NavItem.propTypes = {
  setActiveCards: PropTypes.func.isRequired,
  handleActiveCard: PropTypes.func.isRequired,
  activeCards: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  isActiveCart: PropTypes.bool,
  isFirstLevel: PropTypes.bool,
  onClick: PropTypes.func,
  toggleStatus: PropTypes.func,
  className: PropTypes.string,
}

export default function NavItem({
  data,
  activeCards,
  setActiveCards,
  isActiveCart = false,
  isFirstLevel = false,
  handleActiveCard,
  onClick = undefined,
  toggleStatus = undefined,
  className = undefined,
}) {
  const router = useRouter()
  const { locale, locales } = router
  const isLink = useMemo(() => data.url && data.url !== '#', [data.url])
  const href = useMemo(() => {
    if(isLink) {
      let href = data.url
      if(!isObject(data.url) && locales.includes(data.url.split('/')[1])) {
        href = { pathname: '/' + data.url.split('/').slice(2).join('/') }
      }
      return href
    }
    return undefined
  }, [data.url, locales, isLink])
  const linkProps = useMemo(() => get(data, 'linkProps', {}), [data.linkProps])
  // what common/router/Link used to resolve for us — the quote header passes a
  // pathname template in `url` and the resolved path in `linkProps.as`
  const isActiveLink = useMemo(() =>
    isLink && (router.asPath === href || router.asPath === href?.pathname || router.asPath === linkProps.as),
  [isLink, href, linkProps.as, router.asPath])
  // the Button widget used to stopPropagation for us
  const handleActive = useCallback((e) => {
    e?.stopPropagation()
    handleActiveCard(data.title)
  }, [data.title, handleActiveCard])
  const handleLinkClick = useCallback((e) => {
    if(isActiveCart && isLink && !data.is_open_in_new_tab) {
      e.preventDefault()
      resetBodyScrollOnNavigate()
      toggleStatus?.()
      router.push(href, undefined, { scroll: true })
    }
  }, [isActiveCart, isLink, href, data.is_open_in_new_tab, toggleStatus, router])
  const subNavs = useMemo(() => !isEmpty(data.subsections) ? data.subsections.map((subSection, index) => (
    <NavItem
      key={index}
      data={subSection}
      activeCards={activeCards}
      setActiveCards={setActiveCards}
      isActiveCart={isActiveCart}
      tealLinks={subSection.is_primary_color}
      handleActiveCard={handleActiveCard}
      toggleStatus={toggleStatus}
    />
  )) : null, [data.subsections, activeCards, setActiveCards, isActiveCart, handleActiveCard, toggleStatus])
  return (
    <Fragment>
      <span
        className={cx(classNames.linkWrapper, className)}
        onClick={onClick}
      >
        {isLink ? (
          <Link
            prefetch={false}
            href={href}
            className={cx(data.is_primary_color && classNames.teal, classNames.link, classNames.cur, isActiveLink && classNames.bold)}
            locale={['default', 'en'].includes(locale) ? 'de' : locale}
            target={data.is_open_in_new_tab ? '_blank' : undefined}
            rel={data.is_nofollow ? 'nofollow' : undefined}
            onClick={handleLinkClick}
            {...linkProps}
          >
            {data.title}
          </Link>
        ) : (
          <span
            className={cx(data.is_primary_color && classNames.teal, classNames.link)}
          >
            {data.title}
          </span>
        )}
        {!isEmpty(data.subsections) && (
          <button
            type="button"
            className={cx(classNames.iconButton, classNames.subIcon)}
            onClick={handleActive}
            aria-label={data.title}
          >
            <FontAwesomeIcon icon="sort-down" className={classNames.iconGlyph} />
          </button>
        )}
      </span>
      {!isEmpty(data.subsections) && (
        <div className={cx(classNames.subSection, activeCards[data.title] && classNames.visible, !isActiveCart && classNames.hidden)} data-qa="submenu">
          <span className={cx(classNames.linkWrapper, classNames.backButton)}>
            <button
              type="button"
              className={classNames.iconButton}
              onClick={handleActive}
              aria-label={data.title}
            >
              <FontAwesomeIcon icon="arrow-left" className={classNames.iconGlyph} />
            </button>
            <span>{data.title}</span>
          </span>
          {isFirstLevel ? (
            <Masonry
              breakpointCols={breakpointCols}
              className={cx(containerClassNames.xl, containerClassNames.padding, classNames.navContainer)}
              columnClassName={classNames.masonryColumn}
            >
              {subNavs}
            </Masonry>
          ) : subNavs}
        </div>
      )}
    </Fragment>
  )
}
